import React, { useMemo, useState } from 'react';
import { errorText } from '../../pages/SoftwarePolicy/policyEdit';
import { gap, list, matchedText, MODE, NEEDS_SCRIPT, scopeText, versionText } from './driftShared';
import { ActionChoice, InfoLink } from './DriftControls';
import { byMachine, canRemove, cannotRemoveBecause } from './driftRemove';
import { SortHeader, useSort } from './sortable';
import { byApplication, inLineApplications, queueable, versionsText } from './applicationDrift';

// What queueing the jobs of each kind of finding does to a machine, for the confirmation.
const DOES = { outdated: 'update', missing: 'install', 'forbidden-present': 'remove', oldcopy: 'remove' };

function GapSection({ app, section, onQueue, onRemove }) {
  const [selected, setSelected] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const withJobs = queueable(section.entries);
  const removable = section.entries.filter((e) => canRemove(e.finding));
  // Update or Remove: update unless no script can update any machine here and something can be removed.
  const [action, setAction] = useState(withJobs.length === 0 && removable.length > 0 ? 'remove' : 'update');
  const removing = action === 'remove';
  const choosable = removing ? removable : withJobs;
  const chosen = choosable.filter((e) => selected[e.machine.id]);
  const allChosen = choosable.length > 0 && chosen.length === choosable.length;
  const info = gap(section.gap);
  const does = removing ? 'remove' : DOES[section.gap];
  const choose = (next) => { setAction(next); setSelected({}); setOutcome(null); };

  const queue = async (kind) => {
    setBusy(true);
    setOutcome(null);
    try {
      if (removing) {
        const answer = await onRemove(kind, chosen.map((e) => ({ deviceIds: [e.machine.deviceId], name: e.finding.name, version: e.finding.installed })));
        setOutcome({ kind, machines: byMachine(answer) });
      }
      else {
        const result = await onQueue(kind, app.key, section.gap, chosen.map((e) => e.machine.deviceId));
        setOutcome({ kind, ...result });
      }
      setSelected({});
    } catch (ex) {
      setOutcome({ kind, error: errorText(ex) });
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  const machines = list(outcome?.machines);
  const queued = machines.reduce((n, m) => n + list(m.queued).length, 0);
  const problems = machines.flatMap((m) => [
    ...list(m.refused).map((r) => ({ id: `r-${m.deviceId}-${r.name}`, text: `Not queued, ${m.computer || m.deviceId}: ${r.error}` })),
    ...list(m.failed).map((r) => ({ id: `f-${m.deviceId}-${r.name}`, text: `Failed, ${m.computer || m.deviceId}: ${r.error}` })),
  ]);

  return (
    <div className="v2-app-section">
      <div className="v2-tools">
        <span className={`v2-pill ${info.tone}`} title={info.meaning}>{`${info.label} ${section.entries.length}`}</span>
        <label className="v2-check">
          <input type="checkbox" aria-label={`Select all ${info.label} machines with a script`} checked={allChosen} disabled={choosable.length === 0 || busy}
            onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(choosable.map((x) => [x.machine.id, true])) : {})} />
          {removing ? `Select all that can be removed (${removable.length})` : `Select all with a script (${withJobs.length})`}
        </label>
        {removable.length > 0 && <ActionChoice value={action} onChange={choose} canUpdate={withJobs.length > 0} disabled={busy} />}
        <button type="button" className="v2-btn" disabled={chosen.length === 0 || busy} onClick={() => queue('check')}>{`Test (${chosen.length})`}</button>
        <button type="button" className="v2-btn primary" disabled={chosen.length === 0 || busy} onClick={() => setConfirming(true)}>{`Apply (${chosen.length})`}</button>
        {busy && <span className="v2-spin" role="status" aria-label="Queueing" />}
      </div>
      {outcome?.error && <div className="v2-alert err" role="alert">{outcome.error}</div>}
      {outcome && !outcome.error && (
        <div className={`v2-alert${problems.length ? ' info' : ''}`} role="status">
          {`${queued} ${outcome.kind === 'check' ? 'test' : 'apply'} ${queued === 1 ? 'job' : 'jobs'} in the queue${machines.length === 1 ? ' of 1 machine' : ` of ${machines.length} machines`}.`}
          {problems.map((p) => <div className="v2-sub" key={p.id}>{p.text}</div>)}
        </div>
      )}
      <div className="v2-items">
        {section.entries.map((e) => (
          <div className="v2-app-machine" key={`${e.machine.id}-${e.finding.name}`}>
            <div>
              <input type="checkbox" aria-label={`Select ${e.machine.computer}`} disabled={!choosable.includes(e) || busy}
                title={removing ? cannotRemoveBecause(e.finding) : (queueable([e]).length ? '' : 'No script can do this yet.')}
                checked={Boolean(selected[e.machine.id])} onChange={(ev) => setSelected({ ...selected, [e.machine.id]: ev.target.checked })} />
            </div>
            <div className="name">{e.machine.computer}</div>
            <div>
              {versionText(e.finding)}
              {matchedText(e.finding) && <div className="v2-sub">{matchedText(e.finding)}</div>}
              {scopeText(e.finding.scope) && <div className="v2-sub">{scopeText(e.finding.scope)}</div>}
            </div>
            <div>{MODE[e.finding.mode] || ''}<div className="v2-sub">{e.finding.decidedBy}</div></div>
            <div>
              {e.finding.note && <div>{e.finding.note}</div>}
              {!removing && !queueable([e]).length && NEEDS_SCRIPT.includes(section.gap) && <div className="v2-sub">No script can do this yet.</div>}
            </div>
          </div>
        ))}
      </div>

      {confirming && (
        <div className="v2-dialog-host">
          <div className="v2-dialog" role="dialog" aria-label={`Apply to ${app.name}`}>
            <h2>{`${does ? does[0].toUpperCase() + does.slice(1) : 'Apply'} ${app.name} on ${chosen.length} ${chosen.length === 1 ? 'machine' : 'machines'}?`}</h2>
            <p>{`A job will be put in the queue of each machine and run for real: it will ${does || 'change'} ${app.name}.`}</p>
            {chosen.map((e) => <div key={e.machine.id}>{e.machine.computer}</div>)}
            <div className="actions">
              <button type="button" className="v2-btn" onClick={() => setConfirming(false)} disabled={busy}>Cancel</button>
              <button type="button" className="v2-btn primary" onClick={() => queue('apply')} disabled={busy}>Apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// The drift report by application: one row per program with how many machines are affected and which versions are installed. A row opens to
// its machines, grouped by the kind of finding; the machines that have a script can be ticked and the application put in their queues as a
// test (dry run) or for real after a confirmation. The page sends the application's key, the kind of finding and the machines, never a script.
export default function ApplicationView({ machines, onQueue, onRemove, showInLine = false }) {
  const withFindings = useMemo(() => byApplication(machines), [machines]);
  const inLine = useMemo(() => inLineApplications(machines), [machines]);
  const found = showInLine ? [...withFindings, ...inLine] : withFindings;
  const [open, setOpen] = useState(null);
  const { sort, toggle: sortBy, apply } = useSort();
  const apps = apply(found, {
    application: { get: (a) => a.name },
    findings: { get: (a) => a.gaps.reduce((n, g) => n + g.entries.length, 0) },
    machines: { get: (a) => a.machineCount },
    versions: { get: (a) => a.versions.length },
    newest: { get: (a) => a.available || '' },
  });

  if (apps.length === 0) return <div className="v2-empty">{showInLine ? 'No application has been reported yet.' : 'No application has findings.'}</div>;
  return (
    <table className="v2-table" aria-label="Software drift by application">
      <thead>
        <tr>
          <th style={{ width: 30 }} />
          <SortHeader k="application" sort={sort} onSort={sortBy}>Application</SortHeader>
          <SortHeader k="findings" sort={sort} onSort={sortBy}>Findings</SortHeader>
          <SortHeader k="machines" sort={sort} onSort={sortBy}>Machines</SortHeader>
          <SortHeader k="versions" sort={sort} onSort={sortBy}>Installed versions</SortHeader>
          <SortHeader k="newest" sort={sort} onSort={sortBy}>Newest</SortHeader>
        </tr>
      </thead>
      <tbody>
        {apps.filter((a) => !open || open === a.key).map((a) => {
          const isOpen = open === a.key;
          const toggle = () => setOpen(isOpen ? null : a.key);
          return (
            <React.Fragment key={a.key}>
              <tr className="row" aria-expanded={isOpen} tabIndex={0} onClick={toggle}
                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); toggle(); } }}>
                <td><span className="v2-caret" aria-hidden="true">&#9656;</span></td>
                <td style={{ fontWeight: 700 }}>{a.name}<InfoLink wingetId={a.wingetId} /><div className="v2-sub">{a.key}</div></td>
                <td>{a.inLine && <span className="v2-pill ok" title="Every machine that has it is in line with the policy.">in line</span>}{a.gaps.map((g) => <span key={g.gap} className={`v2-pill ${gap(g.gap).tone}`} title={gap(g.gap).meaning}>{`${gap(g.gap).label} ${g.entries.length}`}</span>)}</td>
                <td>{a.machineCount}</td>
                <td>{versionsText(a.versions)}</td>
                <td>{a.available || ''}</td>
              </tr>
              {isOpen && (
                <tr className="detail">
                  <td colSpan={6}>
                    <div className="v2-detail">
                      <div className="v2-detail-head"><h3>{`Machines with ${a.name}`}</h3></div>
                      {a.inLine && a.entries.map((e) => <div key={e.machine.id}>{e.machine.computer}<span className="v2-sub">{` ${e.installed}`}</span></div>)}
                      {a.gaps.map((g) => <GapSection key={g.gap} app={a} section={g} onQueue={onQueue} onRemove={onRemove} />)}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
